/** A deterministic, non-persistent event/banquet quote. Monetary values are euros. */
export interface EventQuotePerGuestLine {
  id: string;
  costPerGuest: number | null;
}

export interface EventQuoteFixedLine {
  id: string;
  amount: number | null;
}

export interface EventQuoteLaborLine {
  id: string;
  hours: number;
  loadedHourlyRate: number;
  minHeadcount: number;
  guestsPerWorker?: number;
}

export interface EventQuoteInput {
  guests: number;
  grossPricePerGuest: number;
  guestVatRatePct: number;
  grossRoomRevenue?: number;
  roomVatRatePct?: number;
  targetProfit?: number;
  menuLines?: readonly EventQuotePerGuestLine[];
  variableLines?: readonly EventQuotePerGuestLine[];
  fixedLines?: readonly EventQuoteFixedLine[];
  eventLines?: readonly EventQuoteFixedLine[];
  structureLines?: readonly EventQuoteFixedLine[];
  laborLines?: readonly EventQuoteLaborLine[];
  /** Simulation cap for break-even search. Defaults to 10,000 guests. */
  maxBreakEvenGuests?: number;
}

export interface EventQuoteCategoryTotals {
  menu: number | null;
  variable: number | null;
  fixed: number | null;
  event: number | null;
  structure: number | null;
  labor: number;
}

export interface EventQuote {
  guests: number;
  status: 'complete' | 'incomplete';
  missingLineIds: readonly string[];
  categoryTotals: EventQuoteCategoryTotals;
  totalCost: number | null;
  costPerGuest: number | null;
  grossRevenue: number;
  netRevenue: number;
  /** Ricavo netto meno materie/menu e altri costi per ospite. */
  materialMargin: number | null;
  /** Margine di contribuzione dopo il personale diretto. */
  contribution: number | null;
  /** Contribuzione dell'evento dopo gli altri costi diretti/fissi evento. */
  eventContribution: number | null;
  /** Risultato completo dopo la quota struttura. */
  operatingResult: number | null;
  /** Alias del risultato operativo, mantenuto per i consumatori del simulatore. */
  margin: number | null;
  marginPct: number | null;
  targetNetPricePerGuest: number | null;
  targetGrossPricePerGuest: number | null;
  breakEvenGuests: number | null;
}

export interface EventQuoteGuestScenarios {
  min: EventQuote;
  expected: EventQuote;
  max: EventQuote;
}

const DEFAULT_BREAK_EVEN_CAP = 10_000;

function requiredNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) throw new RangeError(`${name} must be a finite non-negative number`);
}

function positiveInteger(value: number, name: string): void {
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 1) {
    throw new RangeError(`${name} must be a positive integer`);
  }
}

function validate(input: EventQuoteInput): void {
  positiveInteger(input.guests, 'guests');
  requiredNonNegative(input.grossPricePerGuest, 'grossPricePerGuest');
  requiredNonNegative(input.guestVatRatePct, 'guestVatRatePct');
  requiredNonNegative(input.grossRoomRevenue ?? 0, 'grossRoomRevenue');
  requiredNonNegative(input.roomVatRatePct ?? 0, 'roomVatRatePct');
  requiredNonNegative(input.targetProfit ?? 0, 'targetProfit');
  positiveInteger(input.maxBreakEvenGuests ?? DEFAULT_BREAK_EVEN_CAP, 'maxBreakEvenGuests');
  for (const line of [...(input.menuLines ?? []), ...(input.variableLines ?? [])]) {
    if (line.costPerGuest != null) requiredNonNegative(line.costPerGuest, `line ${line.id}`);
  }
  for (const line of [...(input.fixedLines ?? []), ...(input.eventLines ?? []), ...(input.structureLines ?? [])]) {
    if (line.amount != null) requiredNonNegative(line.amount, `line ${line.id}`);
  }
  for (const line of input.laborLines ?? []) {
    requiredNonNegative(line.hours, `labor ${line.id} hours`);
    requiredNonNegative(line.loadedHourlyRate, `labor ${line.id} loadedHourlyRate`);
    if (!Number.isInteger(line.minHeadcount) || line.minHeadcount < 0) throw new RangeError(`labor ${line.id} minHeadcount must be a non-negative integer`);
    if (line.guestsPerWorker != null && (!Number.isFinite(line.guestsPerWorker) || line.guestsPerWorker <= 0)) {
      throw new RangeError(`labor ${line.id} guestsPerWorker must be a finite positive number`);
    }
  }
}

function perGuestTotal(lines: readonly EventQuotePerGuestLine[], guests: number, missing: string[]): number | null {
  if (lines.some((line) => line.costPerGuest == null)) {
    missing.push(...lines.filter((line) => line.costPerGuest == null).map((line) => line.id));
    return null;
  }
  return lines.reduce((total, line) => total + line.costPerGuest! * guests, 0);
}

function fixedTotal(lines: readonly EventQuoteFixedLine[], missing: string[]): number | null {
  if (lines.some((line) => line.amount == null)) {
    missing.push(...lines.filter((line) => line.amount == null).map((line) => line.id));
    return null;
  }
  return lines.reduce((total, line) => total + line.amount!, 0);
}

function laborTotal(lines: readonly EventQuoteLaborLine[], guests: number): number {
  return lines.reduce((total, line) => {
    const staffed = line.guestsPerWorker == null ? line.minHeadcount : Math.max(line.minHeadcount, Math.ceil(guests / line.guestsPerWorker));
    return total + staffed * line.hours * line.loadedHourlyRate;
  }, 0);
}

function calculate(input: EventQuoteInput, includeBreakEven: boolean): EventQuote {
  const missing: string[] = [];
  const categories: EventQuoteCategoryTotals = {
    menu: perGuestTotal(input.menuLines ?? [], input.guests, missing),
    variable: perGuestTotal(input.variableLines ?? [], input.guests, missing),
    fixed: fixedTotal(input.fixedLines ?? [], missing),
    event: fixedTotal(input.eventLines ?? [], missing),
    structure: fixedTotal(input.structureLines ?? [], missing),
    labor: laborTotal(input.laborLines ?? [], input.guests),
  };
  const known = [categories.menu, categories.variable, categories.fixed, categories.event, categories.structure];
  const totalCost = known.some((value) => value == null) ? null
    : categories.labor + categories.menu! + categories.variable! + categories.fixed! + categories.event! + categories.structure!;
  const roomGross = input.grossRoomRevenue ?? 0;
  const roomNet = roomGross / (1 + (input.roomVatRatePct ?? 0) / 100);
  const grossRevenue = input.grossPricePerGuest * input.guests + roomGross;
  const netRevenue = input.grossPricePerGuest * input.guests / (1 + input.guestVatRatePct / 100)
    + roomNet;
  const materialMargin = categories.menu == null || categories.variable == null
    ? null : netRevenue - categories.menu - categories.variable;
  const contribution = materialMargin == null ? null : materialMargin - categories.labor;
  const eventContribution = contribution == null || categories.fixed == null || categories.event == null
    ? null : contribution - categories.fixed - categories.event;
  const operatingResult = eventContribution == null || categories.structure == null
    ? null : eventContribution - categories.structure;
  const targetNetPricePerGuest = totalCost == null ? null
    : Math.max(0, (totalCost + (input.targetProfit ?? 0) - roomNet) / input.guests);
  const breakEvenGuests = includeBreakEven && totalCost != null ? findBreakEvenGuests(input) : null;
  return {
    guests: input.guests,
    status: missing.length === 0 ? 'complete' : 'incomplete', missingLineIds: missing,
    categoryTotals: categories, totalCost, costPerGuest: totalCost == null ? null : totalCost / input.guests,
    grossRevenue, netRevenue, materialMargin, contribution, eventContribution, operatingResult,
    margin: operatingResult,
    marginPct: operatingResult == null || netRevenue === 0 ? null : operatingResult / netRevenue * 100,
    targetNetPricePerGuest,
    targetGrossPricePerGuest: targetNetPricePerGuest == null ? null : targetNetPricePerGuest * (1 + input.guestVatRatePct / 100),
    breakEvenGuests,
  };
}

function findBreakEvenGuests(input: EventQuoteInput): number | null {
  const cap = input.maxBreakEvenGuests ?? DEFAULT_BREAK_EVEN_CAP;
  let stableFrom: number | null = null;
  for (let guests = 1; guests <= cap; guests += 1) {
    const quote = calculate({ ...input, guests }, false);
    if (quote.operatingResult != null && quote.operatingResult >= 0) {
      stableFrom ??= guests;
    } else {
      stableFrom = null;
    }
  }
  return stableFrom;
}

export function computeEventQuote(input: EventQuoteInput): EventQuote {
  validate(input);
  return calculate(input, true);
}

/** Calculates the same quote at conservative, expected, and optimistic guest counts. */
export function computeEventQuoteGuestScenarios(
  input: Omit<EventQuoteInput, 'guests'>,
  guests: { min: number; expected: number; max: number },
): EventQuoteGuestScenarios {
  return {
    min: computeEventQuote({ ...input, guests: guests.min }),
    expected: computeEventQuote({ ...input, guests: guests.expected }),
    max: computeEventQuote({ ...input, guests: guests.max }),
  };
}
